import { X } from 'lucide-react'
import { Drawer as AntDrawer, ConfigProvider } from 'antd'
import type { DrawerProps as AntDrawerProps } from 'antd'
import clsx from 'clsx'
import type { ReactNode } from 'react'
import { Button } from '@/components/ui/button'

export interface DrawerProps extends Omit<
  AntDrawerProps,
  'title' | 'footer' | 'open' | 'onClose'
> {
  open: boolean
  onClose: () => void
  title?: ReactNode
  footer?: ReactNode
  size?: number | string
  children?: ReactNode
}

export function Drawer({
  open,
  onClose,
  title,
  footer,
  size = 800,
  destroyOnHidden = true,
  closable = false,
  extra,
  children,
  ...rest
}: DrawerProps) {
  const renderedTitle =
    typeof title === 'string' ? (
      <span className="text-lg font-bold tracking-wide">{title}</span>
    ) : (
      title
    )

  const renderedExtra = extra ?? (
    <Button
      variant="ghost"
      size="icon"
      className="h-8 w-8"
      onClick={onClose}
      aria-label="Close"
    >
      <X />
    </Button>
  )

  return (
    <ConfigProvider
      theme={{
        components: {
          Drawer: {
            colorBgElevated: '#ffffff',
          },
        },
      }}
    >
      <AntDrawer
        open={open}
        onClose={onClose}
        size={size}
        destroyOnHidden={destroyOnHidden}
        title={renderedTitle}
        footer={footer}
        closable={closable}
        extra={renderedExtra}
        {...rest}
      >
        {children}
      </AntDrawer>
    </ConfigProvider>
  )
}

interface DrawerSectionProps {
  title?: ReactNode
  children: ReactNode
  className?: string
  bodyClassName?: string
}

export function DrawerSection({
  title,
  children,
  className,
  bodyClassName,
}: DrawerSectionProps) {
  return (
    <div
      className={clsx(
        'rounded-xl border border-gray-300 border-solid bg-white mb-4 last:mb-0',
        className,
      )}
    >
      {title && <div className="px-4 py-5 text-lg font-semibold">{title}</div>}
      <div className={clsx('px-4', bodyClassName)}>{children}</div>
    </div>
  )
}

interface DrawerDetailListProps {
  children: ReactNode
  className?: string
}

export function DrawerDetailList({
  children,
  className,
}: DrawerDetailListProps) {
  return (
    <div
      className={clsx(
        'rounded-xl border border-gray-300 border-solid bg-white px-4 py-2',
        className,
      )}
    >
      {children}
    </div>
  )
}

interface DrawerDetailRowProps {
  label: ReactNode
  value: ReactNode
  className?: string
  valueClassName?: string
  alignTop?: boolean
}

export function DrawerDetailRow({
  label,
  value,
  className,
  valueClassName,
  alignTop = false,
}: DrawerDetailRowProps) {
  return (
    <div
      className={clsx(
        'flex justify-between gap-4 border-b border-gray-300 py-3 last:border-b-0',
        alignTop ? 'items-start' : 'items-center',
        className,
      )}
    >
      <span className="shrink-0 text-sm font-medium">{label}</span>

      <div
        className={clsx(
          'text-sm font-medium break-words min-w-0',
          alignTop ? 'text-justify ml-6' : 'text-right',
          valueClassName,
        )}
      >
        {value}
      </div>
    </div>
  )
}

interface DrawerFooterProps {
  children: ReactNode
  className?: string
}

export function DrawerFooter({ children, className }: DrawerFooterProps) {
  return (
    <div className={clsx('flex justify-end gap-2', className)}>{children}</div>
  )
}
